import { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import worldTopo from "world-atlas/countries-110m.json";
import { Globe2 } from "lucide-react";
import api from "@/lib/api";

countries.registerLocale(en);

const flag = (code) => code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

function WorldMap({ byNumeric, max, onHover }) {
  return (
    <ComposableMap projectionConfig={{ scale: 150 }} width={800} height={400} style={{ width: "100%", height: "auto" }}>
      <Geographies geography={worldTopo}>
        {({ geographies }) => geographies.map((g) => {
          const hit = byNumeric[String(g.id).padStart(3, "0")];
          const t = hit ? 0.25 + 0.75 * (hit.views / max) : 0;
          return (
            <Geography key={g.rsmKey} geography={g}
              onMouseEnter={() => onHover(hit ? `${hit.name}: ${hit.views.toLocaleString()} views` : g.properties.name)}
              onMouseLeave={() => onHover("")}
              style={{
                default: { fill: hit ? `rgba(245,179,66,${t})` : "#1f2937", stroke: "#0b0d12", strokeWidth: 0.5, outline: "none" },
                hover: { fill: hit ? "#fbbf24" : "#374151", stroke: "#0b0d12", strokeWidth: 0.5, outline: "none" },
                pressed: { outline: "none" },
              }} />
          );
        })}
      </Geographies>
    </ComposableMap>
  );
}

export function CountryViews({ items, days }) {
  const [target, setTarget] = useState("");
  const [data, setData] = useState(null);
  const [hover, setHover] = useState("");

  useEffect(() => {
    const params = { ...(days !== "0" ? { days } : {}), ...(target ? { stream_id: target } : {}) };
    api.get("/analytics/countries", { params }).then((r) => setData(r.data)).catch(() => setData({ countries: [], unknown: 0, total: 0 }));
  }, [target, days]);

  const rows = useMemo(() => (data?.countries || []).map((c) => ({ ...c, name: countries.getName(c.code, "en") || c.name })), [data]);
  const byNumeric = useMemo(() => Object.fromEntries(rows.map((r) => [countries.alpha2ToNumeric(r.code), r]).filter(([k]) => k)), [rows]);
  const max = Math.max(1, ...rows.map((r) => r.views));
  const located = rows.reduce((s, r) => s + r.views, 0);

  return (
    <div data-testid="views-by-country" className="bg-card border border-gray-800 rounded-2xl p-5 mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-base font-semibold flex items-center gap-2"><Globe2 className="h-4 w-4 text-amber-400" /> Views by Country</h3>
        <select data-testid="country-stream-select" value={target} onChange={(e) => setTarget(e.target.value)}
          className="bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-gray-200 max-w-xs">
          <option value="">All streams & videos</option>
          {items.map((s) => <option key={s.stream_id} value={s.stream_id}>{s.kind === "video" ? "Video · " : "Live · "}{s.title}</option>)}
        </select>
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="relative rounded-xl bg-gray-950/60 border border-gray-800/60 p-2" data-testid="country-map">
          <WorldMap byNumeric={byNumeric} max={max} onHover={setHover} />
          {hover && <div data-testid="country-map-tooltip" className="absolute top-3 left-3 rounded-md bg-black/80 px-2.5 py-1 text-xs text-white pointer-events-none">{hover}</div>}
        </div>
        <div data-testid="country-list" className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
          {rows.length === 0 ? <p data-testid="country-empty" className="text-sm text-gray-500 py-8 text-center">No country data yet for this selection.</p> : rows.map((r) => (
            <div key={r.code} data-testid={`country-row-${r.code}`} className="flex items-center gap-3 text-sm">
              <span className="w-6 text-lg leading-none">{flag(r.code)}</span>
              <span className="flex-1 min-w-0 truncate text-gray-200">{r.name}</span>
              <div className="w-24 h-1.5 rounded-full bg-gray-800 overflow-hidden hidden sm:block"><div className="h-full bg-amber-400" style={{ width: `${(r.views / max) * 100}%` }} /></div>
              <span className="w-14 text-right font-mono text-gray-300">{r.views.toLocaleString()}</span>
              <span className="w-12 text-right text-xs text-gray-500">{located ? Math.round((r.views / located) * 100) : 0}%</span>
            </div>
          ))}
          {data?.unknown > 0 && <p data-testid="country-unknown" className="text-xs text-gray-500 pt-2 border-t border-gray-800">{data.unknown.toLocaleString()} older view{data.unknown === 1 ? "" : "s"} recorded before country tracking show as unknown.</p>}
        </div>
      </div>
    </div>
  );
}
